import React from 'react';
import { mixed, object, string } from 'yup';
import { Button, Grid, InputAdornment, Paper, Typography } from '@mui/material';
import InputForm from '../../commun/input-form';
import { Link } from 'react-router-dom';
import SelectForm from '../../commun/select-form';
import { ReferenceDto } from '../../../services/dtos/reference.dto';
import { ERROR_MAIL } from '../../../utils/constants.utils';
import { Form, Formik } from 'formik';
import { InterlocutorsReferenceDto } from '../../../services/dtos/interlocutors-reference.dto';
import { ContactUsDto } from '../../../services/dtos/contact-us.dto';
import './contact-us.form.scss';

//Props d'entrée de la fonction ContractAttachementForm
type Props = {
    handlerSubmit: (e: ContactUsDto) => void;
    validate: boolean;
    handleChangeInterlocutor: (interlocutor: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => void;
    isMoral: boolean;
    interlocutors: InterlocutorsReferenceDto[];
};

/**
 * Formulaire de contact
 * @param handlerSubmit Comportement à la soumission du formulaire
 * @param isMoral Permet de savoir quel type de personne
 * @param validate  Etat du formulaire
 * @param handleChangeInterlocutor Handler de modification d'interlocuteur
 * @param interlocutorsSelection Liste d'interlocuteur
 * @constructor
 */
function ContactUsForm({ handlerSubmit, isMoral, validate, handleChangeInterlocutor, interlocutors }: Props) {
    //Constantes
    const messageCaracteresMin = "Merci d'entrer deux caractères minimum";
    const nbCaracteresMin = 2;
    const nbCaracteresTel = 9;

    // Définition des contrôles de validation du formulaire
    // Pour la partie mot de passe, nous avons plusieurs tests pour avoir des messages différents suivant les cas
    // Les vérifications changent en fonction de si l'interlocuteur est une personne morale ou non
    const validationSchema = object({
        //Controle des champs: obligatoire
        type: mixed<ReferenceDto>().required("Le type d'interlocuteur est obligatoire"),
        nom: string().when(() =>
            isMoral
                ? string().trim().required('Le nom est obligatoire').min(nbCaracteresMin, messageCaracteresMin)
                : string()
                      .trim()
                      .required('Le nom est obligatoire')
                      .matches(
                          /^[A-Za-z-áàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ ]+$/,
                          "Seul les caractères alphabétiques, le tiret (-) et l'apostrophe (') sont autorisés"
                      )
                      .min(nbCaracteresMin, messageCaracteresMin)
        ),
        prenom: string().when(() =>
            isMoral
                ? string().notRequired()
                : string()
                      .trim()
                      .matches(
                          /^[A-Za-z-áàâäãåçéèêëíìîïñóòôöõúùûüýÿæœÁÀÂÄÃÅÇÉÈÊËÍÌÎÏÑÓÒÔÖÕÚÙÛÜÝŸÆŒ ]+$/,
                          "Seul les caractères alphabétiques, le tiret - et l'apostrophe ' sont autorisés"
                      )
                      .min(nbCaracteresMin, messageCaracteresMin)
                      .required('Le prénom est obligatoire')
        ),

        //Controle du champ tel portable
        //Controle : obligatoire / taille / format
        tel: string()
            .matches(/^\d+$/, {
                message: 'Caractère non valide inséré.',
            })
            .length(nbCaracteresTel, "Merci d'entrer 9 caractères."),
        //Controle du champ mail
        //Controle : obligatoire / taille / format
        mail: string()
            .email(ERROR_MAIL)
            .matches(/^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i, ERROR_MAIL),
        objet: string().required(),
        message: string().required(),
    });

    //Initialisation de valeurs par defauts
    const iniValues = {
        nom: '',
        prenom: '',
        mail: '',
        tel: '',
        objet: '',
        message: '',
        numeroAbonnement: '',
        numeroFacture: '',
        type: '',
    };

    //On reconstruit la liste car elle n'est pas confirme pour le composant
    const interlocutorsSelection = interlocutors.map(interlocutor => {
        return {
            value: interlocutor.id,
            label: interlocutor.label,
        };
    });

    return (
        //Affichage du formulaire contactez-nous
        //Il y un formulaire qui affiche le champ "prénom" en fonction de si la personne est une personne moral
        <Paper elevation={3} className="paperForm-contactUs">
            <Formik initialValues={iniValues} onSubmit={handlerSubmit} validationSchema={validationSchema}>
                <Form>
                    <Grid container spacing={2}>
                        <Grid item xs={12} md={12} sx={{ textAlign: 'right' }}>
                            <Typography variant={'subtitle2'}>* : champs obligatoires</Typography>
                        </Grid>
                        {/*Champ interlocuteur*/}
                        <Grid item xs={12} md={6}>
                            <SelectForm
                                required={true}
                                values={interlocutorsSelection}
                                handleChange={handleChangeInterlocutor}
                                id="interlocutors"
                                name="type"
                                label="Vous êtes un/une :"
                            />
                        </Grid>
                        <Grid item xs={12} md={6} />
                        {/*Champ nom*/}
                        <Grid item xs={12} md={6}>
                            <InputForm required id="name" name="nom" label="Nom" />
                        </Grid>
                        {/*si != PM : Champ firstname*/}
                        {isMoral ? (
                            <Grid item xs={12} md={6} />
                        ) : (
                            <Grid item xs={12} md={6}>
                                <InputForm required id="firstname" name="prenom" label="Prénom" />
                            </Grid>
                        )}
                        {/*Champ téléphone*/}
                        <Grid item xs={12} md={6}>
                            <InputForm
                                id="mobilePhone"
                                name="tel"
                                label="Téléphone portable"
                                type="tel"
                                required
                                InputProps={{
                                    startAdornment: <InputAdornment position="start">0</InputAdornment>,
                                }}
                                autoComplete={'tel-national'}
                            />
                        </Grid>
                        {/*Champ mail*/}
                        <Grid item xs={12} md={6}>
                            <InputForm
                                id="mailPerso"
                                name="mail"
                                label="Email"
                                type="email"
                                autoComplete={'email'}
                                required
                            />
                        </Grid>
                        {/*Champ Numéro d'abonnement*/}
                        <Grid item xs={12} md={6}>
                            <InputForm id="subscriptionNumber" name="numeroAbonnement" label="Numéro d'abonnement" />
                        </Grid>
                        {/*Champ Numéro de facture*/}
                        <Grid item xs={12} md={6}>
                            <InputForm id="invoiceNumber" name="numeroFacture" label="Numéro de facture" />
                        </Grid>
                        {/*Champ Objet*/}
                        <Grid item xs={12} md={6}>
                            <InputForm id="object" name="objet" label="Objet" required />
                        </Grid>
                        {/*Champ Message*/}
                        <Grid item xs={12}>
                            <InputForm id="message" name="message" label="Message" required multiline rows={3} />
                        </Grid>
                        {/*Emplacement des boutons annuler et envoyer*/}
                        <Grid item xs={0} sm={9} />
                        <Grid item xs={12} sm={3} className={'buttons-form'}>
                            <Typography variant={'button'}>
                                <Button variant="neutral" component={Link} to={'/'}>
                                    Annuler
                                </Button>
                            </Typography>
                            <Typography variant={'button'}>
                                <Button type="submit" variant="contained" disabled={validate}>
                                    Envoyer
                                </Button>
                            </Typography>
                        </Grid>
                    </Grid>
                </Form>
            </Formik>
        </Paper>
    );
}

export default ContactUsForm;
